import type {
  ApplicationCompositionPlan,
  DesignCompositionRelease,
  DesignGap,
} from "./types"
import { SEMANTIC_CONTENT_KEYS } from "./types"

const ADMITTED_SEMANTIC_CONTENT_KEYS = new Set<string>(
  SEMANTIC_CONTENT_KEYS,
)

function gap(
  code: DesignGap["code"],
  path: string,
  message: string,
  release: DesignCompositionRelease,
): DesignGap {
  return { code, path, message, evidenceRefs: [...release.sourceRefs] }
}

interface SlotUsage {
  nodeIndex: number
  state: string
  slotId: string
  cardinality: "zero-or-one" | "exactly-one" | "many"
  count: number
}

function addOccurrence(
  occurrences: Map<string, Map<string, string[]>>,
  scope: string,
  authority: string,
  nodeId: string,
) {
  const byAuthority = occurrences.get(scope) ?? new Map<string, string[]>()
  const nodeIds = byAuthority.get(authority) ?? []
  nodeIds.push(nodeId)
  byAuthority.set(authority, nodeIds)
  occurrences.set(scope, byAuthority)
}

function incrementCount(
  counts: Map<string, Map<string, number>>,
  scope: string,
  authority: string,
) {
  const byAuthority = counts.get(scope) ?? new Map<string, number>()
  byAuthority.set(authority, (byAuthority.get(authority) ?? 0) + 1)
  counts.set(scope, byAuthority)
}

export function evaluateCompositionQuality(
  plan: ApplicationCompositionPlan,
  release: DesignCompositionRelease,
): DesignGap[] {
  const gaps: DesignGap[] = []
  const stateSet = new Set(plan.states)

  if (plan.states.length === 0 || stateSet.size !== plan.states.length) {
    gaps.push(
      gap(
        "plan-state-invalid",
        "states",
        "Plan states must be non-empty and unique.",
        release,
      ),
    )
  }
  if (plan.semanticNodes.length === 0) {
    gaps.push(
      gap(
        "semantic-node-missing",
        "semanticNodes",
        "A composition plan must contain at least one semantic node.",
        release,
      ),
    )
  }

  const firstNodeIndexById = new Map<string, number>()
  plan.semanticNodes.forEach((node, nodeIndex) => {
    const firstIndex = firstNodeIndexById.get(node.nodeId)
    if (firstIndex !== undefined) {
      gaps.push(
        gap(
          "duplicate-node-id",
          `semanticNodes[${nodeIndex}].nodeId`,
          `Node ID ${node.nodeId} duplicates semanticNodes[${firstIndex}].`,
          release,
        ),
      )
    } else {
      firstNodeIndexById.set(node.nodeId, nodeIndex)
    }

    const visibleStateSet = new Set(node.visibleIn)
    if (
      node.visibleIn.length === 0 ||
      visibleStateSet.size !== node.visibleIn.length ||
      node.visibleIn.some((state) => !stateSet.has(state))
    ) {
      gaps.push(
        gap(
          "node-visibility-invalid",
          `semanticNodes[${nodeIndex}].visibleIn`,
          "Node visibility must be non-empty, unique, and limited to declared plan states.",
          release,
        ),
      )
    }
  })

  if (release.authorityStatus !== "consumer-verified") {
    gaps.push(
      gap(
        "release-not-consumer-verified",
        "authorityStatus",
        "Only consumer-verified releases may compile.",
        release,
      ),
    )
  }
  if (plan.target !== release.target) {
    gaps.push(
      gap(
        "target-mismatch",
        "target",
        "Plan and release targets differ.",
        release,
      ),
    )
  }

  const matchingRecipes = release.recipes.filter(
    (recipe) => recipe.recipeId === plan.recipeRef,
  )
  if (matchingRecipes.length === 0) {
    gaps.push(
      gap(
        "recipe-missing",
        "recipeRef",
        `Recipe ${plan.recipeRef} is not admitted.`,
        release,
      ),
    )
    return gaps
  }
  if (matchingRecipes.length > 1) {
    gaps.push(
      gap(
        "recipe-authority-ambiguous",
        "recipes",
        `Recipe ${plan.recipeRef} has ${matchingRecipes.length} authorities.`,
        release,
      ),
    )
    return gaps
  }
  const recipe = matchingRecipes[0]

  const slotUsages: SlotUsage[] = []
  const exclusiveGroupOccurrences = new Map<
    string,
    Map<string, string[]>
  >()
  const capabilityOccurrences = new Map<
    string,
    Map<string, string[]>
  >()
  const primaryCounts = new Map<string, Map<string, number>>()

  plan.semanticNodes.forEach((node, nodeIndex) => {
    const nodePath = `semanticNodes[${nodeIndex}]`
    for (const key of Object.keys(node.content)) {
      if (!ADMITTED_SEMANTIC_CONTENT_KEYS.has(key)) {
        gaps.push(
          gap(
            "raw-presentation-forbidden",
            `${nodePath}.content.${key}`,
            `Content key ${key} is not in the semantic content contract.`,
            release,
          ),
        )
      }
    }

    const matchingRegions = recipe.regions.filter(
      (candidate) => candidate.regionId === node.regionRef,
    )
    if (matchingRegions.length === 0) {
      gaps.push(
        gap(
          "region-missing",
          `${nodePath}.regionRef`,
          `Region ${node.regionRef} is not in the recipe.`,
          release,
        ),
      )
      return
    }
    if (matchingRegions.length > 1) {
      const recipeIndex = release.recipes.indexOf(recipe)
      gaps.push(
        gap(
          "region-authority-ambiguous",
          `recipes[${recipeIndex}].regions`,
          `Region ${node.regionRef} has ${matchingRegions.length} authorities.`,
          release,
        ),
      )
      return
    }
    const region = matchingRegions[0]
    if (!region.acceptsSemanticRoles.includes(node.semanticRole)) {
      gaps.push(
        gap(
          "semantic-role-not-admitted",
          `${nodePath}.semanticRole`,
          `${node.semanticRole} is not admitted in ${node.regionRef}.`,
          release,
        ),
      )
    }

    const matchingComponents = release.components.filter(
      (component) => component.semanticRole === node.semanticRole,
    )
    if (matchingComponents.length === 0) {
      gaps.push(
        gap(
          "component-missing",
          `${nodePath}.semanticRole`,
          `No admitted component implements ${node.semanticRole}.`,
          release,
        ),
      )
      return
    }
    if (matchingComponents.length > 1) {
      gaps.push(
        gap(
          "component-authority-ambiguous",
          "components",
          `${node.semanticRole} has ${matchingComponents.length} component authorities.`,
          release,
        ),
      )
      return
    }
    const component = matchingComponents[0]
    for (const tokenRef of component.tokenRefs) {
      if (!release.tokenRefs.includes(tokenRef)) {
        gaps.push(
          gap(
            "token-missing",
            `${nodePath}.semanticRole`,
            `Token ${tokenRef} is not declared by the release.`,
            release,
          ),
        )
      }
    }

    for (const slot of component.actionSlots) {
      for (const state of node.visibleIn) {
        slotUsages.push({
          nodeIndex,
          state,
          slotId: slot.slotId,
          cardinality: slot.cardinality,
          count: node.action?.slotRef === slot.slotId ? 1 : 0,
        })
      }
    }

    if (!node.action) return
    const matchingSlots = component.actionSlots.filter(
      (candidate) => candidate.slotId === node.action?.slotRef,
    )
    if (matchingSlots.length === 0) {
      gaps.push(
        gap(
          "action-slot-missing",
          `${nodePath}.action.slotRef`,
          `Slot ${node.action.slotRef} is not admitted.`,
          release,
        ),
      )
      return
    }
    if (matchingSlots.length > 1) {
      const componentIndex = release.components.indexOf(component)
      gaps.push(
        gap(
          "action-slot-authority-ambiguous",
          `components[${componentIndex}].actionSlots`,
          `Slot ${node.action.slotRef} has ${matchingSlots.length} authorities.`,
          release,
        ),
      )
      return
    }
    const slot = matchingSlots[0]
    if (!slot.acceptsRoles.includes(node.action.role)) {
      gaps.push(
        gap(
          "action-role-not-admitted",
          `${nodePath}.action.role`,
          `${node.action.role} is not allowed in ${slot.slotId}.`,
          release,
        ),
      )
    }
    if (!slot.capabilityRefs.includes(node.action.capabilityRef)) {
      gaps.push(
        gap(
          "capability-not-admitted",
          `${nodePath}.action.capabilityRef`,
          `${node.action.capabilityRef} is not admitted in ${slot.slotId}.`,
          release,
        ),
      )
    }
    const actionTokenRef = release.actionTokenBindings[node.action.role]
    if (!release.tokenRefs.includes(actionTokenRef)) {
      gaps.push(
        gap(
          "token-missing",
          `${nodePath}.action.role`,
          `Action token ${actionTokenRef} is not declared by the release.`,
          release,
        ),
      )
    }
    if (
      node.semanticRole === "recovery-action" &&
      node.action.role === "destructive"
    ) {
      gaps.push(
        gap(
          "action-semantic-mismatch",
          `${nodePath}.action.role`,
          "Recovery actions are not destructive.",
          release,
        ),
      )
    }

    for (const state of node.visibleIn) {
      addOccurrence(
        exclusiveGroupOccurrences,
        state,
        slot.exclusiveGroup,
        node.nodeId,
      )
      addOccurrence(
        capabilityOccurrences,
        state,
        node.action.capabilityRef,
        node.nodeId,
      )

      if (node.action.role === "primary") {
        incrementCount(primaryCounts, state, region.regionId)
      }
    }
  })

  for (const [state, byGroup] of exclusiveGroupOccurrences) {
    for (const [exclusiveGroup, nodeIds] of byGroup) {
      if (nodeIds.length > 1) {
        gaps.push(
          gap(
            "exclusive-action-conflict",
            `states.${state}.exclusiveGroups.${exclusiveGroup}`,
            `Exclusive action is bound by ${nodeIds.join(", ")}.`,
            release,
          ),
        )
      }
    }
  }
  for (const [state, byCapability] of capabilityOccurrences) {
    for (const [capabilityRef, nodeIds] of byCapability) {
      if (nodeIds.length > 1) {
        gaps.push(
          gap(
            "exclusive-action-conflict",
            `states.${state}.capabilities.${capabilityRef}`,
            `Capability is bound by ${nodeIds.join(", ")} in one state.`,
            release,
          ),
        )
      }
    }
  }
  for (const usage of slotUsages) {
    const violates =
      (usage.cardinality === "zero-or-one" && usage.count > 1) ||
      (usage.cardinality === "exactly-one" && usage.count !== 1)
    if (violates) {
      gaps.push(
        gap(
          "action-slot-cardinality",
          `semanticNodes[${usage.nodeIndex}].actionSlots.${usage.slotId}.visibleIn.${usage.state}`,
          `Slot ${usage.slotId} requires ${usage.cardinality}; received ${usage.count}.`,
          release,
        ),
      )
    }
  }
  for (const [state, byRegion] of primaryCounts) {
    for (const [regionRef, count] of byRegion) {
      const region = recipe.regions.find(
        (candidate) => candidate.regionId === regionRef,
      )
      if (region && count > region.maxPrimaryActions) {
        gaps.push(
          gap(
            "primary-action-cardinality",
            `states.${state}.regions.${regionRef}`,
            `${count} primary actions exceed ${region.maxPrimaryActions} in region ${regionRef}.`,
            release,
          ),
        )
      }
    }
  }

  return gaps
}
